import { SYSTEM_TEMPLATES } from '@/shared/data/templates';
import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { supabaseService } from '@/shared/services/supabaseService';
import { resendService } from '@/features/email/services/resendService';

/**
 * System configuration and helper methods
 * Handles system-wide configuration and utility functions
 */

// Cloudinary will be imported lazily when needed
let cloudinaryService: any = null;
const getCloudinaryService = async () => {
  if (!cloudinaryService) {
    const module = await import('@/shared/services/cloudinaryService');
    cloudinaryService = module.cloudinaryService;
  }
  return cloudinaryService;
};

/**
 * Configuration service
 */
export const configService = {
  /**
   * Get system configuration
   */
  getSystemConfig() {
    return {
      maintenanceMode: false,
      appUrl: 'http://localhost:3000',
      appName: 'SwaySuite',
      demoVideoId: '',
      demoVideoUrl: 'https://res.cloudinary.com/demo/video/upload/v1690554536/samples/cld-sample-video.mp4',
      platformLogoUrl: '/SS-Logo-main.png',
      resendFromEmail: 'noreply@swaysuite.com',

      // Supabase (from environment variables)
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL || 'https://ipofqysutswnumkhqgbm.supabase.co',
      supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
      supabaseServiceRoleKey: import.meta.env.SUPABASE_SERVICE_ROLE_KEY || '',
      supabaseJwtSecret: import.meta.env.SUPABASE_JWT_SECRET || '',
      supabaseAccessToken: import.meta.env.SUPABASE_ACCESS_TOKEN || '',

      // Cloudinary
      cloudinaryCloudName: 'demo',
      cloudinaryApiKey: 'demo',
      cloudinaryApiSecret: 'demo',

      // Firebase
      firebaseApiKey: 'demo',
      firebaseAuthDomain: 'demo',
      firebaseProjectId: 'demo',
      firebaseStorageBucket: 'demo',
      firebaseMessagingSenderId: 'demo',
      firebaseAppId: 'demo',
      firebaseMeasurementId: 'demo',

      // Payments
      paypalClientId: 'demo',
      paypalClientSecret: 'demo',
      stripePublicKey: '',

      // Regional Payments
      mpesaConsumerKey: '',
      mpesaConsumerSecret: '',
      mpesaShortcode: '174379',
      mpesaPasskey: '',
      mpesaPhoneNumber: '',

      payoneerApiKey: '',
      payoneerClientId: '',
      payoneerClientSecret: '',

      // Notifications
      telegramBotToken: '',
      telegramChatId: '',
      resendApiKey: ''
    };
  },

  /**
   * Get service status
   */
  async getServiceStatus() {
    let cloudinaryStatus = false;
    try {
      const cloudinaryServiceInstance = await getCloudinaryService();
      cloudinaryStatus = await cloudinaryServiceInstance.checkConfiguration();
    } catch (error) {
      cloudinaryStatus = false;
    }

    return {
      firebase: !!firebaseAuthService,
      supabase: !!supabaseService,
      cloudinary: cloudinaryStatus,
      resend: !!resendService
    };
  },

  /**
   * Format currency amount
   */
  formatCurrency(amount: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  },

  /**
   * Format date
   */
  formatDate(date: Date | string, format: 'short' | 'long' | 'time' = 'short'): string {
    const d = typeof date === 'string' ? new Date(date) : date;

    switch (format) {
      case 'long':
        return d.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      case 'time':
        return d.toLocaleTimeString('en-US');
      default:
        return d.toLocaleDateString('en-US');
    }
  },

  /**
   * Generate unique ID
   */
  generateId(prefix?: string): string {
    const p = prefix || '';
    return `${p}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Get platform profile
   */
  getPlatformProfile(): any {
    return {
      name: 'SwaySuite',
      logoUrl: '/SS-Logo-main.png',
      brandColor: '#3B82F6'
    };
  },

  /**
   * Get logo URL
   */
  getLogoUrl(light?: boolean): string {
    return light ? '/SS-Logo-white.png' : '/SS-Logo-main.png';
  },

  /**
   * Get plan limits
   */
  getPlanLimits(planId: string): any {
    const enterpriseLimits = {
      maxClients: -1, // Unlimited
      maxInvoices: -1, // Unlimited
      maxServices: -1, // Unlimited
      maxClientEdits: -1, // Unlimited edits
      canEditCompanyName: true,
      canEditClientName: true,
      canRemoveBranding: true
    };

    const limits: Record<string, any> = {
      free: {
        maxClients: 3,
        maxInvoices: 10,
        maxServices: 5,
        maxClientEdits: 1, // Free: Can only edit client details once
        canEditCompanyName: false,
        canEditClientName: false,
        canRemoveBranding: false
      },
      basic: {
        maxClients: 50,
        maxInvoices: 100,
        maxServices: 25,
        maxClientEdits: 3, // Basic: Limited edits (3)
        canEditCompanyName: false,
        canEditClientName: true,
        canRemoveBranding: false
      },
      pro: {
        maxClients: 500,
        maxInvoices: 1000,
        maxServices: 100,
        maxClientEdits: -1, // Pro: Unlimited edits
        canEditCompanyName: true,
        canEditClientName: true,
        canRemoveBranding: true
      },
      enterprise: enterpriseLimits,
      company: enterpriseLimits // Alias for enterprise
    };
    return limits[planId] || limits.free;
  },

  /**
   * Get system templates
   */
  getTemplates(): any[] {
    // Return the complete system templates with all properties
    return SYSTEM_TEMPLATES;
  },

  /**
   * Check if using real services (for compatibility)
   */
  get isUsingRealServices(): boolean {
    return true;
  }
};
